import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { excursions } from "@/mocks/excursions";
import { useSupabasePrices } from "@/hooks/useSupabasePrices";
import ExcursionCard from "@/components/feature/ExcursionCard";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const ALL_KEY = "all";

export default function ExcursionsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get("category") || ALL_KEY;
  const [activeCategory, setActiveCategory] = useState<string>(categoryFromUrl);

  // Sync URL param changes (e.g. browser back/forward)
  useEffect(() => {
    setActiveCategory(categoryFromUrl);
  }, [categoryFromUrl]);

  const { data: excursionsWithPrices } = useSupabasePrices(excursions);

  // Derive unique category keys from data (preserve order of first appearance)
  const categoryKeys = useMemo(() => {
    const seen = new Set<string>();
    const keys: string[] = [];
    excursionsWithPrices.forEach((e) => {
      if (!seen.has(e.categoryKey)) {
        seen.add(e.categoryKey);
        keys.push(e.categoryKey);
      }
    });
    return keys;
  }, [excursionsWithPrices]);

  const filtered = useMemo(
    () =>
      activeCategory === ALL_KEY
        ? excursionsWithPrices
        : excursionsWithPrices.filter((e) => e.categoryKey === activeCategory),
    [activeCategory, excursionsWithPrices],
  );

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      {/* Page header */}
      <div className="bg-background-100 border-b border-background-200 pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-accent-600 font-medium mb-3">
            {t("exall_badge")}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
            {t("exall_heading")}
          </h1>
          <p className="text-foreground-600 text-base md:text-lg max-w-2xl">
            {t("exall_subtitle")}
          </p>
        </div>
      </div>

      {/* Filters + grid */}
      <main className="flex-1 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">

          {/* Category pills */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button
              onClick={() => setActiveCategory(ALL_KEY)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeCategory === ALL_KEY
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200"
              }`}
            >
              {t("exall_filter_all")}
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                activeCategory === ALL_KEY
                  ? "bg-background-50/20 text-background-50"
                  : "bg-background-200 text-foreground-600"
              }`}>
                {excursionsWithPrices.length}
              </span>
            </button>

            {categoryKeys.map((key) => {
              const count = excursionsWithPrices.filter((e) => e.categoryKey === key).length;
              const isActive = activeCategory === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200"
                  }`}
                >
                  {t(key)}
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? "bg-background-50/20 text-background-50"
                      : "bg-background-200 text-foreground-600"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Results count */}
          <p className="text-foreground-500 text-sm mb-6">
            {filtered.length} {t("exall_results_label")}
          </p>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((e) => (
              <ExcursionCard key={e.slug} excursion={e} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                <i className="ri-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{t("exall_no_results")}</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}